import { useState } from "react";

import { toast } from "sonner";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

import {
  useBvaBudgets,
  useBvaClasses,
  useBvaGrants,
  useBvaSummary,
  useBvaUsers,
  useSetBudget,
  useSetGrants,
  type UserGrants,
} from "../api";

/**
 * Admin tab: who sees which department, and which QB budget backs the year.
 *
 * Grants are per-user class sets; "Revenue" is the virtual class that unlocks
 * revenue-to-budget. Admins (budget_vs_actual.admin) bypass grants entirely —
 * this tab manages everyone else.
 */
export function AccessConfigTab({ year }: { year: number }) {
  const grants = useBvaGrants(true);
  const users = useBvaUsers(true);
  const classes = useBvaClasses(true, year);

  return (
    <div className="flex flex-col gap-6">
      <GrantsCard
        grants={grants.data?.grants ?? []}
        users={users.data?.users ?? []}
        classes={classes.data?.classes ?? []}
        loading={grants.isLoading || users.isLoading || classes.isLoading}
        error={grants.error ? String(grants.error) : null}
      />
      <BudgetPickerCard year={year} />
    </div>
  );
}

function GrantsCard({
  grants,
  users,
  classes,
  loading,
  error,
}: {
  grants: UserGrants[];
  users: { user_id: string; email: string; name: string }[];
  classes: string[];
  loading: boolean;
  error: string | null;
}) {
  const setGrants = useSetGrants();
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string> | null>(null);

  if (loading) return <Skeleton className="h-48" />;
  if (error) {
    return (
      <Alert variant="danger">
        <AlertTitle>Couldn't load grants</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const granted = new Map(grants.map((g) => [g.user_id, g]));
  const userOptions: ComboOption[] = users.map((u) => ({
    id: u.user_id,
    label: u.name || u.email,
    subtitle: u.email,
    badge: granted.has(u.user_id)
      ? `${granted.get(u.user_id)!.classes.length} granted`
      : undefined,
  }));

  const editing = selectedUser
    ? (draft ?? new Set(granted.get(selectedUser)?.classes ?? []))
    : null;

  const toggle = (cls: string) => {
    if (!editing) return;
    const next = new Set(editing);
    if (next.has(cls)) next.delete(cls);
    else next.add(cls);
    setDraft(next);
  };

  const save = () => {
    if (!selectedUser || !editing) return;
    setGrants.mutate(
      { user_id: selectedUser, classes: [...editing] },
      {
        onSuccess: (res) => {
          toast.success(
            res.classes.length
              ? `${res.name || res.email} now sees: ${res.classes.join(", ")}`
              : `All access removed for ${res.name || res.email}`,
          );
          setDraft(null);
        },
        onError: (e) => toast.error(String(e)),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Department access</CardTitle>
        <p className="text-sm text-muted-foreground">
          Each user sees only the departments (QuickBooks classes) granted here.
          Grant <b>Revenue</b> to unlock revenue-to-budget. Admins always see
          everything.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {grants.length > 0 && (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Sees</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g) => (
                  <tr
                    key={g.user_id}
                    className="cursor-pointer border-b border-border/60 hover:bg-muted/40"
                    onClick={() => {
                      setSelectedUser(g.user_id);
                      setDraft(null);
                    }}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium">{g.name || g.email}</div>
                      <div className="text-xs text-muted-foreground">{g.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {g.classes.map((c) => (
                          <Badge key={c} variant={c === "Revenue" ? "info" : "neutral"}>
                            {c}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 rounded-md border p-3">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            Edit a user's access
          </Label>
          <Combobox
            options={userOptions}
            value={selectedUser}
            onChange={(id) => {
              setSelectedUser(id);
              setDraft(null);
            }}
            placeholder="Pick a user…"
            className="max-w-md"
          />
          {selectedUser && editing && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {classes.map((cls) => (
                  <label
                    key={cls}
                    className="flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm hover:bg-muted/40"
                  >
                    <Checkbox checked={editing.has(cls)} onCheckedChange={() => toggle(cls)} />
                    <span>{cls}</span>
                    {cls === "Revenue" && (
                      <Badge variant="info" className="ml-auto">
                        virtual
                      </Badge>
                    )}
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={save} disabled={setGrants.isPending || draft == null}>
                  Save access
                </Button>
                {draft != null && (
                  <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                    Discard changes
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function BudgetPickerCard({ year }: { year: number }) {
  const budgets = useBvaBudgets(true);
  const setBudget = useSetBudget();
  // The summary is already cached from the dashboard; it carries the resolved
  // budget + whether it's pinned.
  const summary = useBvaSummary(year, false);
  const current = summary.data?.budget ?? null;

  const options: ComboOption[] = (budgets.data?.budgets ?? []).map((b) => ({
    id: String(b.budget_id),
    label: b.budget_name,
    subtitle: `${b.start_date} → ${b.end_date}`,
    badge: b.is_active ? undefined : "inactive",
  }));

  const apply = (id: string | null) => {
    setBudget.mutate(
      { year, budget_id: id },
      {
        onSuccess: (res) =>
          toast.success(
            res.source === "override"
              ? `${year} pinned to "${res.name}"`
              : `${year} back to auto-pick ("${res.name}")`,
          ),
        onError: (e) => toast.error(String(e)),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Budget for {year}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Auto-pick uses the newest active "Approved Budget" for the year.
          QuickBooks revisions land as new budgets — pin one here if Finance
          revises mid-year. Clearing the pick reverts to auto.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {current && (
          <p className="text-sm">
            Currently: <b>{current.name}</b>{" "}
            <Badge variant={current.source === "override" ? "info" : "neutral"}>
              {current.source === "override" ? "pinned" : "auto"}
            </Badge>
          </p>
        )}
        {budgets.isLoading ? (
          <Skeleton className="h-9 max-w-md" />
        ) : (
          <Combobox
            options={options}
            value={current?.source === "override" ? current.id : null}
            onChange={apply}
            placeholder="Pin a budget (or leave on auto)…"
            className="max-w-md"
            disabled={setBudget.isPending}
          />
        )}
      </CardContent>
    </Card>
  );
}
